import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import {AD_IDS_ITEM} from '@/app/main/authentication/pages/SignUp/models/constants';
import {getCoParentInvitationFromStorage} from '@/app/main/coparent/utils';
import {StepNumber} from '@/app/shared-components';
import {useRemoteFeatureFlags} from '@/dataSource/src/api/firebase/useRemoteFeatureFlags';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import logError from '@/utils/errors/logError';

import {
  KYC_APPROVED_ANIMATION,
  KYC_APPROVED_CO_PARENT_STEP_ITEMS,
  KYC_APPROVED_LOGIC,
  KYC_APPROVED_STEP_ITEMS,
} from './constants';
import {useTrackers} from './trackers';

// region [---- TYPES ----]
export interface IKycApprovedStep {
  id: string;
  number: StepNumber;
  label: string;
  isCompleted: boolean;
  showConnector: boolean;
}

type ViewModelContextType = {
  handleDurationEnds: () => void;
  steps: IKycApprovedStep[];
  isProgressButtonVisible: boolean;
};
// endregion

// region [---- CONTEXT ----]
const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);
// endregion

// region [---- PROVIDER ----]
export function ViewModelProvider({children}: {children: ReactNode}) {
  // region [---- HOOKS ----]
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {profile, refetchProfile} = useUsersRepository();
  const {trackViewKycApproved} = useTrackers();
  const {flagsMap} = useRemoteFeatureFlags();
  const [isProgressButtonVisible, setIsProgressButtonVisible] = useState(false);
  const isCoParentInvitation = useMemo(
    () => !!getCoParentInvitationFromStorage(),
    [],
  );
  const stepItems = isCoParentInvitation
    ? KYC_APPROVED_CO_PARENT_STEP_ITEMS
    : KYC_APPROVED_STEP_ITEMS;
  // endregion

  // region [---- HELPERS ----]
  const parseAdIds = useCallback((adIdsString: string, from: string) => {
    try {
      if (!adIdsString) return null;
      return JSON.parse(adIdsString);
    } catch (error) {
      logError(error, {
        customTitle: 'KYC Approved - Error parsing ad ids',
        fileName: 'KycApproved - ViewModelContext.tsx',
        functionName: 'parseAdIds',
        data: {
          adIdsString: adIdsString,
          from: from,
        },
      });
      return null;
    }
  }, []);
  // endregion

  // region [---- LOGIC ----]
  const getAdIds = useCallback(() => {
    const localStorageDataAdIds = localStorage.getItem(AD_IDS_ITEM);
    const profileMetadataAdIds = profile?.metadata?.AD_CLICK_IDS_V1;
    const adIdsFromLocalStorage = parseAdIds(
      localStorageDataAdIds,
      'localStorage',
    );
    const adIdsFromProfile = parseAdIds(
      profileMetadataAdIds,
      'profile metadata',
    );
    const adIdsMetadata = adIdsFromProfile || adIdsFromLocalStorage || {};
    return adIdsMetadata;
  }, [parseAdIds, profile?.metadata?.AD_CLICK_IDS_V1]);

  const tryRefreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      logError(error, {
        fileName: 'KycApproved ViewModelContext',
        functionName: 'tryRefreshProfile',
      });
    }
  }, [refetchProfile]);

  const loadKycApproved = useCallback(async () => {
    await tryRefreshProfile();
    const adIds = getAdIds();
    trackViewKycApproved({
      adIds: adIds || undefined,
    });
  }, [getAdIds, tryRefreshProfile, trackViewKycApproved]);
  // endregion

  // region [---- EFFECTS ----]
  useEffect(() => {
    loadKycApproved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lastStepIndex = Math.max(
      stepItems.length - KYC_APPROVED_LOGIC.STEP_LAST_INDEX_OFFSET,
      KYC_APPROVED_LOGIC.MIN_STEP_INDEX,
    );
    const lastBadgeDelay =
      KYC_APPROVED_ANIMATION.ITEM_BADGE_BASE_DELAY +
      lastStepIndex * KYC_APPROVED_ANIMATION.ITEM_BADGE_STAGGER_DELAY;
    const lastTextDelay =
      lastBadgeDelay + KYC_APPROVED_ANIMATION.ITEM_TEXT_DELAY_OFFSET;
    const stepsAnimationEnd =
      lastTextDelay + KYC_APPROVED_ANIMATION.ITEM_TEXT_DURATION;
    const titleAnimationEnd =
      KYC_APPROVED_ANIMATION.TITLE_DELAY +
      KYC_APPROVED_ANIMATION.TITLE_DURATION;
    const lottieAnimationEnd = KYC_APPROVED_ANIMATION.LOTTIE_DURATION;
    const allAnimationsEndMs =
      Math.max(stepsAnimationEnd, titleAnimationEnd, lottieAnimationEnd) *
      KYC_APPROVED_LOGIC.MILLISECONDS_PER_SECOND;
    const delayMs = Math.max(
      KYC_APPROVED_ANIMATION.BUTTON_REVEAL_TARGET_MS,
      allAnimationsEndMs + KYC_APPROVED_ANIMATION.BUTTON_REVEAL_DELAY_MS,
    );

    const timer = window.setTimeout(() => {
      setIsProgressButtonVisible(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [stepItems.length]);
  // endregion

  // region [---- ACTIONS ----]
  const handleDurationEnds = useCallback(async () => {
    await tryRefreshProfile();
    if (flagsMap?.onboardingV5Enabled) {
      navigate('/onboarding/download-app?previousScreen=kyc-approved');
    } else {
      navigate('/onboarding/add-kid/personal-info');
    }
  }, [flagsMap?.onboardingV5Enabled, navigate, tryRefreshProfile]);
  // endregion

  // region [---- MEMOS ----]
  const steps = useMemo<IKycApprovedStep[]>(
    () =>
      stepItems.map(step => ({
        id: step.id,
        number: step.number,
        label: t(step.translationKey),
        isCompleted: step.isCompleted,
        showConnector: step.showConnector,
      })),
    [stepItems, t],
  );

  const value = useMemo(
    () => ({
      handleDurationEnds,
      steps,
      isProgressButtonVisible,
    }),
    [handleDurationEnds, isProgressButtonVisible, steps],
  );
  // endregion

  return (
    <ViewModelContext.Provider value={value}>
      {children}
    </ViewModelContext.Provider>
  );
}
// endregion

// region [---- CONTEXT HOOK ----]
export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('KycApproved ViewModelContext is undefined');
  }
  return context;
}
// endregion
